// eslint-disable-next-line no-unused-vars -- 3.4.2026
import React, { useState, useRef, useEffect } from 'react';
import '../styles/CookieForm.css';
import { lengthUnitLabel, weightUnitLabel, densityUnitLabel } from '../utils/unitConversion';
import { lengthUnitWarning, weightUnitWarning } from '../utils/unitWarnings';

const CookieForm = ({ 
  title, 
  width, 
  length, 
  weightNoIcing, 
  weight, 
  count, 
  onChange,
  onReset,
  disabled = false, 
  hideTitle = false,
  formId = 'cookie-form',
  isMetric = false,
  isMetricWeight = true
 }) => {
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const formType = weight !== undefined ? 'ref' : 'new';

  // Unit-mismatch warnings: set on blur, cleared on any keystroke
  const [warnings, setWarnings] = useState({});
  const clearWarning = (field) => setWarnings(prev => {
    if (!prev[field]) return prev;
    const next = { ...prev }; delete next[field]; return next;
  });

  // Component state and handlers
  const handleReset = () => {
    if (onReset) {
      // Caller handles reset directly (bypasses onChange/blur)
      onReset();
      setWarnings({});
      return;
    }
    // Fallback: reset all fields via onChange
    if (weight !== undefined) {
      onChange('width', '');
      onChange('length', '');
      onChange('weightNoIcing', '');
      onChange('weight', '');
    } else {
      onChange('width', '');
      onChange('length', '');
      onChange('count', '1');
    }
  };
  const getStep = () => '0.25';
  const getWeightStep = () => '0.1';

  const validateAndFormatValue = (field, value) => {
    // Allow empty fields during typing
    if (value === '' || value === '-') {
      return value;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return '';
    }

    // Always make positive
    const positiveValue = Math.abs(numValue);

    // For count, ensure it's a positive integer (rounded up)
    if (field === 'count') {
      return Math.max(1, Math.ceil(positiveValue)).toString();
    }

    // For other fields, ensure positive number with max 2 decimal places
    return (Math.round(positiveValue * 100) / 100).toString();
  };

  const handleBlur = (field, value) => {
    // Evaluate unit warning on blur (field exit)
    if (field === 'width' || field === 'length') {
      const w = lengthUnitWarning(value, isMetric);
      setWarnings(prev => w ? { ...prev, [field]: w } : (({ [field]: _, ...rest }) => rest)(prev));
    } else if (field === 'weightNoIcing' || field === 'weight') {
      const w = weightUnitWarning(value, isMetricWeight);
      setWarnings(prev => w ? { ...prev, [field]: w } : (({ [field]: _, ...rest }) => rest)(prev));
    }
    if (value === '') return; // Don't override a cleared field
    const formattedValue = validateAndFormatValue(field, value);
    // Only update if there's a change to avoid unnecessary re-renders
    if (formattedValue !== value) {
      onChange(field, formattedValue || '');
    }
  };

  return (
    <div className="cookie-form">
      {!hideTitle && (
        <div className="form-header">
          <div className="form-title-section">
            <h2>{title}</h2>
          </div>
          <button 
            className="form-header-clear-btn"
            onClick={handleReset}
            title={`Clear ${title}`}
          >
            <svg className="button-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            <span className="button-label">Clear</span>
          </button>
        </div>
      )}
      <div className="input-group">
        <div className="input-field">
          <label htmlFor={`${formId}-width`}>
            Width: <span className="unit-label">({lengthUnitLabel(isMetric)})</span>
          </label>
          <input
            id={`${formId}-width`}
            type="number"
            value={width}
            onChange={(e) => { clearWarning('width'); onChange('width', e.target.value); }}
            onBlur={(e) => handleBlur('width', e.target.value)}
            step={getStep()}
            min="0"
            required
            disabled={disabled}
          />
          {!disabled && warnings.width && (
            <div className="unit-warning">{warnings.width}</div>
          )}
        </div>
        <div className="input-field">
          <label htmlFor={`${formId}-length`}>
            Length: <span className="unit-label">({lengthUnitLabel(isMetric)})</span>
          </label>
          <input
            id={`${formId}-length`}
            type="number"
            value={length}
            onChange={(e) => { clearWarning('length'); onChange('length', e.target.value); }}
            onBlur={(e) => handleBlur('length', e.target.value)}
            step={getStep()}
            min="0"
            required
            disabled={disabled}
          />
          {!disabled && warnings.length && (
            <div className="unit-warning">{warnings.length}</div>
          )}
        </div>
        {weight !== undefined && (
          <>
            <div className="input-field">
              <label htmlFor={`${formId}-weight-no-icing`}>
                Cookie weight before icing: <span className="weight-unit">({weightUnitLabel(isMetricWeight)})</span>
              </label>
              <input
                id={`${formId}-weight-no-icing`}
                type="number"
                value={weightNoIcing}
                onChange={(e) => { clearWarning('weightNoIcing'); onChange('weightNoIcing', e.target.value); }}
                onBlur={(e) => handleBlur('weightNoIcing', e.target.value)}
                step={getWeightStep()}
                min="0"
                required
                disabled={disabled}
              />
              {!disabled && warnings.weightNoIcing && (
                <div className="unit-warning">{warnings.weightNoIcing}</div>
              )}
            </div>
            <div className="input-field">
              <label htmlFor={`${formId}-weight`}>
                Cookie weight after icing: <span className="weight-unit">({weightUnitLabel(isMetricWeight)})</span>
              </label>
              <input
                id={`${formId}-weight`}
                type="number"
                value={weight}
                onChange={(e) => { clearWarning('weight'); onChange('weight', e.target.value); }}
                onBlur={(e) => handleBlur('weight', e.target.value)}
                step={getWeightStep()}
                min="0"
                required
                disabled={disabled}
              />
              {!disabled && warnings.weight && (
                <div className="unit-warning">{warnings.weight}</div>
              )}
            </div>
            <div className="input-field calculated-field">
              <label htmlFor={`${formId}-icing-used`}>
                Total Icing on Cookie: <span className="weight-unit">({weightUnitLabel(isMetricWeight)})</span>
              </label>

              <input
                id={`${formId}-icing-used`}
                type="number"
                value={(() => {
                  // Only calculate if both weights have valid values
                  if (!weight || !weightNoIcing || weight === '0') return '';
                  const w = parseFloat(weight);
                  const wNoIcing = parseFloat(weightNoIcing);
                  if (isNaN(w) || isNaN(wNoIcing)) return '';
                  return Math.max(0, (w - wNoIcing)).toFixed(2);
                })()}
                readOnly
                disabled
              />
              {/* Read-only fields don't get a debug ID */}
            </div>
            <div className="input-field calculated-field">
              <label htmlFor={`${formId}-icing-per-area`}>
                Icing Coverage Rate: <span className="weight-unit">({densityUnitLabel(isMetric, isMetricWeight)})</span>
              </label>

              <input
                id={`${formId}-icing-per-area`}
                type="number"
                value={(() => {
                  // Only calculate if we have all required values
                  if (!width || !length || !weight || weight === '0') return '';
                  
                  const w = parseFloat(width);
                  const l = parseFloat(length);
                  const weightVal = parseFloat(weight);
                  const weightNoIcingVal = parseFloat(weightNoIcing || '0');
                  
                  // Validate all values
                  if (isNaN(w) || isNaN(l) || isNaN(weightVal) || isNaN(weightNoIcingVal)) return '';
                  
                  const area = w * l;
                  if (area <= 0) return '';
                  
                  // Ensure we don't have negative icing weight
                  const icingUsed = Math.max(0, weightVal - weightNoIcingVal);
                  
                  const perArea = (icingUsed / area).toFixed(4);
                  return perArea;
                })()}
                readOnly
                disabled
              />
              {/* Read-only fields don't get a debug ID */}
            </div>
          </>
        )}
        {count !== undefined && (
          <div className="input-field">
            <label htmlFor={`${formId}-count`}>Number of Cookies:</label>
            <input
              id={`${formId}-count`}
              type="number"
              value={count}
              onChange={(e) => onChange('count', e.target.value)}
              onBlur={(e) => handleBlur('count', e.target.value)}
              min="1"
              step="1"
              required
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieForm;
